import { useState } from "react";
import { MEDICATIONS } from "@/data/medications";

// Clean product-image renderer for a medication card.
// Uses the real per-medication artwork from /assets/meds/<slug>.png and gracefully
// falls back to a generic bottle/vial render when a bespoke asset isn't present.
// NO text label is drawn over the artwork — the medication name lives in the
// text area below the image (single, clear title) to avoid a cramped, duplicated look.
function ProductImage({ med, large = false }) {
  const primary = `/assets/meds/${med.slug}.png`;
  const fallback = med.bottleType === "bottle" ? "/assets/pill-bottle.png" : "/assets/vial-base.png";
  const [src, setSrc] = useState(primary);
  const height = large ? "max-h-[300px] md:max-h-[360px]" : "max-h-[150px]";
  return (
    <img
      src={src}
      alt={`${med.name} — ${med.type}`}
      width={large ? 360 : 150}
      height={large ? 360 : 150}
      loading="lazy"
      onError={() => setSrc((cur) => (cur !== fallback ? fallback : cur))}
      className={`${height} w-auto object-contain drop-shadow-[0_18px_26px_rgba(0,0,0,0.45)]`}
      draggable={false}
    />
  );
}

export default function MedCard({ slug, dense = false }) {
  const m = MEDICATIONS[slug];
  if (!m) return null;
  const accentColor =
    m.accent === "blue"
      ? "var(--leen-blue)"
      : m.accent === "yellow"
      ? "var(--leen-yellow)"
      : m.accent === "amber"
      ? "#D67D3E"
      : "var(--leen-pink)";

  return (
    <a
      href={`/medications/${m.slug}`}
      data-testid={`med-card-${m.slug}`}
      className="group bg-[var(--leen-card)] border border-[var(--leen-line)] hover:border-[var(--leen-pink)] rounded-xl p-5 md:p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
      style={{ "--hover-accent": accentColor }}
    >
      {/* IMAGE AREA — clean, centered, contained product artwork with breathing room.
          A soft light panel behind the image keeps white-background product photos
          looking intentional (no floating seam) on the dark card surface. */}
      <div className="h-[180px] w-full flex items-center justify-center mb-5 rounded-xl overflow-hidden"
        style={{ background: "linear-gradient(180deg, #F7F8FB 0%, #EEF1F6 100%)" }}>
        <ProductImage med={m} />
      </div>

      {/* TEXT AREA — single clear title + form descriptor. */}
      <h3 className="font-display text-[15px] md:text-[16px] text-white tracking-wide leading-tight">
        {m.name}
      </h3>
      <p className="mt-1 text-[12px] text-[var(--leen-ink-mute)] italic mb-4">
        {m.type}
      </p>
      {!dense && (
        <ul className="space-y-1.5 text-left mb-5 w-full">
          {m.bullets.map((b) => (
            <li key={b} className="text-[12px] text-[var(--leen-ink-soft)] flex items-start gap-1.5">
              <span style={{ color: accentColor }} className="mt-0.5">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-auto pt-3 border-t border-[var(--leen-line-soft)] w-full">
        <span
          className="eyebrow inline-flex items-center gap-1.5"
          style={{ color: accentColor }}
        >
          Learn More <span aria-hidden>→</span>
        </span>
      </div>
    </a>
  );
}

// Back-compat export: some pages render a standalone product image.
export function Vial({ med, large = false }) {
  return (
    <div className={`relative mx-auto flex items-center justify-center ${large ? "w-[300px] md:w-[360px]" : "w-[150px]"}`}>
      <ProductImage med={med} large={large} />
    </div>
  );
}
